import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'

/** The parts of an address, as the studio holds them. */
export type StructuredAddress = {
	street1: string
	street2: string | null
	city: string
	state: string
	zip: string
}

/**
 * An address as it is written down, rather than as a database column joins it.
 *
 * The flat `address` string is one line the server happens to have run together; a member
 * standing outside needs the door, and a suite or a building name is the half of that a street
 * number cannot give. So the site name leads where there is one, the street takes its own lines,
 * and the city closes — which is how an address is written everywhere but in a column.
 *
 * Lifted out of the scheduling sheet, which had the only copy. Two screens draw a studio now —
 * the one that books it and the one that reminds you of it — and they should not disagree about
 * how an address looks.
 */
export const StudioAddress = ({
	address,
	siteName,
	size = 'body0',
}: {
	address: StructuredAddress
	siteName?: string | null
	size?: 'body0' | 'body1'
}) => (
	<VStack gap={2}>
		<Text size={size}>{address.street1}</Text>
		{!!address.street2 && <Text size={size}>{address.street2}</Text>}
		<Text size={size}>{`${address.city}, ${address.state} ${address.zip}`}</Text>
		{/* "Inside WeWork", the phrasing the web check-in page uses, and only where there is a
		    site to be inside. It closes the address rather than leading it: the street is what
		    gets a member to the block, and the building name is what gets them through the door
		    once they are standing on it. */}
		{!!siteName && (
			<Text size={size} tone="soft">
				{`Inside ${siteName}`}
			</Text>
		)}
	</VStack>
)
